import { Phone, Mail, MapPin, Clock, Facebook, Instagram, MessageCircle } from 'lucide-react';
import { ASSETS, CONTACT } from '../components/Layout';

const ContactPage = () => {
  return (
    <div className="min-h-screen bg-cream" data-testid="contact-page">
      {/* Hero Section */}
      <section className="relative h-[50vh] min-h-[350px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={ASSETS.outdoor}
            alt="Bean in Stony outdoor garden seating"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-cream" />
        </div>
        
        <div className="relative z-10 text-center px-6">
          <p className="font-accent text-white text-2xl mb-4 drop-shadow-lg">We'd Love to Hear From You</p>
          <h1 
            className="font-serif text-5xl md:text-7xl text-white font-bold drop-shadow-[0_4px_8px_rgba(0,0,0,0.5)]"
            data-testid="contact-headline"
          >
            Get in Touch
          </h1>
        </div>
      </section>

      {/* Contact Content */}
      <section className="section-padding" data-testid="contact-content-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
            {/* WhatsApp Section */}
            <div>
              <h2 className="font-serif text-3xl md:text-4xl text-walnut mb-6">
                Message Us on WhatsApp
              </h2>
              <p className="text-walnut/70 mb-8">
                Have a question about our menu or planning an event? 
                Scan the QR code below to start a conversation with us directly on WhatsApp.
              </p>

              <div className="p-8 bg-[#25D366]/10 border border-[#25D366]/30 rounded-lg">
                <div className="flex items-center gap-3 mb-6">
                  <MessageCircle className="w-8 h-8 text-[#25D366]" />
                  <span className="font-serif text-2xl text-walnut">WhatsApp</span>
                </div>
                
                {/* QR Code for Desktop */}
                <div className="hidden md:block">
                  <p className="text-walnut/70 mb-4 text-sm text-center">
                    Scan the QR code to start a conversation
                  </p>
                  <div className="flex justify-center">
                    <img 
                      src={CONTACT.whatsappQR}
                      alt="WhatsApp QR Code - Scan to message Bean in Stony"
                      className="w-64 h-64 rounded-lg shadow-heritage"
                      data-testid="whatsapp-qr"
                    />
                  </div>
                </div>

                {/* WhatsApp Button for Mobile */}
                <div className="md:hidden text-center">
                  <p className="text-walnut/70 mb-4 text-sm">
                    Tap below to message us directly on WhatsApp
                  </p>
                  <a
                    href="https://wa.me/447703977404"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-3 bg-[#25D366] text-white px-6 py-3 rounded-full font-medium hover:bg-[#128C7E] transition-colors"
                    data-testid="whatsapp-button"
                  >
                    <MessageCircle className="w-5 h-5" />
                    Chat on WhatsApp
                  </a>
                  <p className="text-walnut/60 mt-4 text-sm">
                    Or call/text: <a href="tel:+447703977404" className="font-medium text-walnut hover:text-sage">+44 7703 977404</a>
                  </p>
                </div>

                <p className="text-center text-walnut/60 mt-4 text-sm hidden md:block">
                  Or message us at: <span className="font-medium text-walnut">+44 7703 977404</span>
                </p>
              </div>
            </div>

            {/* Contact Info */}
            <div>
              <div className="bg-walnut p-8 md:p-12 rounded-lg text-cream sticky top-32">
                <h2 className="font-serif text-3xl mb-8">Visit Us</h2>

                <div className="space-y-6">
                  {/* Phone */}
                  <a 
                    href={`tel:${CONTACT.phone}`}
                    className="flex items-center gap-4 text-cream/90 hover:text-sage transition-colors group"
                    data-testid="contact-info-phone"
                  >
                    <div className="w-12 h-12 bg-cream/10 rounded-full flex items-center justify-center group-hover:bg-sage/20 transition-colors">
                      <Phone className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-sm text-cream/60 uppercase tracking-wider">Phone</p>
                      <p className="text-lg font-medium">{CONTACT.phone}</p>
                    </div>
                  </a>

                  {/* Email */}
                  <a 
                    href={`mailto:${CONTACT.email}`}
                    className="flex items-center gap-4 text-cream/90 hover:text-sage transition-colors group"
                    data-testid="contact-info-email"
                  >
                    <div className="w-12 h-12 bg-cream/10 rounded-full flex items-center justify-center group-hover:bg-sage/20 transition-colors">
                      <Mail className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-sm text-cream/60 uppercase tracking-wider">Email</p>
                      <p className="text-lg font-medium">{CONTACT.email}</p>
                    </div>
                  </a>

                  {/* Buffet Email */}
                  <a 
                    href={`mailto:${CONTACT.buffetEmail}`}
                    className="flex items-center gap-4 text-cream/90 hover:text-sage transition-colors group"
                    data-testid="contact-info-buffet-email"
                  >
                    <div className="w-12 h-12 bg-cream/10 rounded-full flex items-center justify-center group-hover:bg-sage/20 transition-colors">
                      <Mail className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-sm text-cream/60 uppercase tracking-wider">Buffet & Catering</p>
                      <p className="text-lg font-medium">{CONTACT.buffetEmail}</p>
                    </div>
                  </a>

                  {/* Address */}
                  <div className="flex items-start gap-4 text-cream/90">
                    <div className="w-12 h-12 bg-cream/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-sm text-cream/60 uppercase tracking-wider">Address</p>
                      <p className="text-lg font-medium">{CONTACT.address}</p>
                    </div>
                  </div>

                  {/* Hours */}
                  <div className="flex items-start gap-4 text-cream/90">
                    <div className="w-12 h-12 bg-cream/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <Clock className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-sm text-cream/60 uppercase tracking-wider">Opening Hours</p>
                      <p className="text-lg font-medium">Mon - Sat: 8am - 4pm</p>
                      <p className="text-cream/70">Sunday: Closed</p>
                    </div>
                  </div>
                </div>

                {/* Social Links */}
                <div className="mt-10 pt-8 border-t border-cream/20">
                  <p className="text-sm text-cream/60 uppercase tracking-wider mb-4">Follow Us</p>
                  <div className="flex gap-4">
                    <a
                      href={CONTACT.facebook}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-12 h-12 bg-cream/10 hover:bg-sage rounded-full flex items-center justify-center transition-all duration-300 hover:-translate-y-1"
                      data-testid="contact-social-facebook"
                    >
                      <Facebook className="w-5 h-5" />
                    </a>
                    <a
                      href={CONTACT.instagram}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-12 h-12 bg-cream/10 hover:bg-sage rounded-full flex items-center justify-center transition-all duration-300 hover:-translate-y-1"
                      data-testid="contact-social-instagram"
                    >
                      <Instagram className="w-5 h-5" />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="section-padding bg-stone-light/30" data-testid="map-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="font-serif text-3xl md:text-4xl text-walnut mb-4">
              Find Us
            </h2>
            <p className="text-walnut/70">
              Located in the heart of Stony Stratford's historic High Street
            </p>
          </div>
          <div className="w-full h-[400px] rounded-lg overflow-hidden shadow-heritage-lg">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2448.4!2d-0.853!3d52.057!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4877b0c0e0e0e0e0%3A0x0!2s75%20High%20St%2C%20Stony%20Stratford%2C%20Milton%20Keynes%20MK11%201AY!5e0!3m2!1sen!2suk!4v1"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Bean in Stony Location"
              data-testid="google-map"
            />
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
